// Image optimisation helper.
// Routes image URLs through a free, CDN-backed image proxy (wsrv.nl, Cloudflare)
// that resizes on-the-fly, converts to WebP, and caches for a year.
//
// This lets us keep the original high-res assets in storage while serving
// smaller, faster images to browsers — no build step, no re-uploads.
//
// Usage:
//   import { imgSrc } from '@/lib/img';
//   <img src={imgSrc(product.image, { w: 1200 })} />
//
// Presets are provided for common contexts:
//   imgSrc(url, 'hero')      -> 1600w
//   imgSrc(url, 'main')      -> 1200w  (PDP main viewer)
//   imgSrc(url, 'card')      -> 800w   (Shop/landing product cards)
//   imgSrc(url, 'thumb')     -> 400w   (thumbnails, cart lines)
//   imgSrc(url, 'avatar')    -> 200w   (very small)

const PROXY = 'https://wsrv.nl/';

// Which hosts should be routed through the proxy. External CDN images (Shopify,
// Cloudinary, etc.) are already optimised; SVGs and data URLs must never be
// touched.
const PROXY_HOSTS = ['customer-assets.emergentagent.com'];

const PRESETS = {
  hero:  { w: 1600, q: 82 },
  main:  { w: 1200, q: 82 },
  card:  { w: 800,  q: 82 },
  thumb: { w: 400,  q: 82 },
  avatar:{ w: 200,  q: 80 },
};

/**
 * Return an optimised URL for the given image source.
 *
 * @param {string} src            Original image URL (or data:/blob:/undefined).
 * @param {string|object} opts    Preset name ("hero" | "main" | "card" | "thumb" | "avatar")
 *                                 OR an options object { w, h, q, fit, output }.
 * @returns {string} Optimised URL, or the original if optimisation isn't applicable.
 */
export function imgSrc(src, opts = 'main') {
  if (!src || typeof src !== 'string') return src;
  // Don't touch data URLs, blob URLs, or SVGs (already tiny + resolution-independent)
  if (src.startsWith('data:') || src.startsWith('blob:')) return src;
  if (src.endsWith('.svg') || src.includes('.svg?')) return src;

  let host = '';
  try {
    host = new URL(src).host;
  } catch {
    return src; // relative URLs — bail out
  }
  if (!PROXY_HOSTS.some((h) => host.endsWith(h))) return src;

  const cfg = typeof opts === 'string' ? PRESETS[opts] || PRESETS.main : { ...PRESETS.main, ...opts };
  const params = new URLSearchParams();
  params.set('url', src);
  if (cfg.w) params.set('w', String(cfg.w));
  if (cfg.h) params.set('h', String(cfg.h));
  if (cfg.q) params.set('q', String(cfg.q));
  params.set('output', cfg.output || 'webp');
  if (cfg.fit) params.set('fit', cfg.fit);
  // sensible defaults: only downscale, never upscale
  params.set('we', ''); // don't enlarge
  return `${PROXY}?${params.toString()}`;
}

/**
 * Build a srcSet string with two densities (1x + 2x) at the target width.
 * Useful for high-DPR displays without blowing the byte budget.
 */
export function imgSrcSet(src, opts = 'main') {
  if (!src || typeof src !== 'string') return undefined;
  const cfg = typeof opts === 'string' ? PRESETS[opts] || PRESETS.main : { ...PRESETS.main, ...opts };
  if (!cfg.w) return undefined;
  const w1x = imgSrc(src, { ...cfg, w: cfg.w });
  const w2x = imgSrc(src, { ...cfg, w: cfg.w * 2 });
  // If proxy wasn't applied (e.g., SVG), fall through to undefined
  if (w1x === src) return undefined;
  return `${w1x} 1x, ${w2x} 2x`;
}
